// @ts-nocheck

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Eye, FilePlus2, Loader2, Pencil, Plus, RefreshCw, Settings2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import Loader from '@/components/partials/components/Loader';
import EditKnowledgeBaseDialog from './components/EditKnowledgeBaseDialog';
import { KbDialogContent, KbDialogHead } from './components/Kbdialogparts';
import AddDocumentDialog from './components/AdddocumentDialog';
import {
  listKnowledgeBases,
  getKnowledgeBaseStatus,
  deleteKnowledgeBase,
} from '@/service/knowledgeBaseApi';
import { getUser } from '@/utilities/setting';
import { MODULE_ACL, USER_ROLES, ROUTES } from '@/utilities/constant';
import { hasPermission } from '@/utilities/helpers';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const STATUS_VARIANT: Record<string, any> = {
  completed: 'success',
  ready: 'success',
  success: 'success',
  failed: 'destructive',
  error: 'destructive',
  processing: 'warning',
  pending: 'warning',
  ingesting: 'warning',
};

function statusBadgeVariant(status?: string) {
  if (!status) return 'outline';
  return STATUS_VARIANT[String(status).toLowerCase()] || 'secondary';
}

function pick(obj: any, keys: string[], fallback = '—') {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== '') {
      return obj[key];
    }
  }
  return fallback;
}

function kbId(row: any) {
  return pick(row, ['id', 'knowledge_base_id', 'kb_id'], '');
}

function kbName(row: any) {
  return pick(row, ['name', 'kb_name']);
}

function kbType(row: any) {
  return pick(row, ['kb_type', 'type'], null);
}

function kbStatus(row: any) {
  return pick(row, ['overall_status', 'status', 'ingestion_status'], null);
}

function kbCreatedAt(row: any) {
  return pick(row, ['created_at', 'created_on', 'created'], null);
}

function kbUpdatedAt(row: any) {
  return pick(row, ['updated_at', 'updated_on', 'modified_at', 'last_updated'], null);
}

const KnowledgeBasePage = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<any>(null);
  const [pendingAddDoc, setPendingAddDoc] = useState<any>(null);
  const [detailsModal, setDetailsModal] = useState<any>(null);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [aclLoaded, setAclLoaded] = useState(false);

  const user = getUser();
  const isSuperAdmin = user?.role === USER_ROLES.SUPERADMIN;

  useEffect(() => {
    const applyAcl = () => {
      const ops = getUser()?.acl_settings?.module_operations;
      if (!ops) return;
      try {
        setLoggedInUserACL(typeof ops === 'string' ? parseModuleOperations(ops) : ops);
        setAclLoaded(true);
      } catch (e) {
        console.log('Failed to parse ACL', e);
      }
    };

    applyAcl();
    window.addEventListener('acl-refreshed', applyAcl);
    fetchList();
    return () => window.removeEventListener('acl-refreshed', applyAcl);
  }, []);

  const enrichStatuses = (list: any[]) => {
    list.forEach((row) => {
      const id = kbId(row);
      if (!id) return;
      getKnowledgeBaseStatus(id)
        .then((resp: any) => {
          const nextStatus = pick(resp, ['overall_status', 'status'], undefined);
          if (nextStatus === undefined) return;
          setRows((prev) =>
            prev.map((r) => (kbId(r) === id ? { ...r, overall_status: nextStatus } : r)),
          );
        })
        .catch(() => {});
    });
  };

  const fetchList = () => {
    setLoading(true);
    listKnowledgeBases()
      .then((resp: any) => {
        const list = Array.isArray(resp) ? resp : [];
        setRows(list);
        enrichStatuses(list);
      })
      .catch((err: any) => {
        toast.error(typeof err === 'string' ? err : 'Failed to load knowledge bases');
      })
      .finally(() => setLoading(false));
  };


  const isCallCentreAdmin = user?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const aclAllows = (op: string) =>
    !aclLoaded || hasPermission(loggedInUserACL, MODULE_ACL.AI_AGENT, op);

  const canRead =
    isSuperAdmin ||
    isCallCentreAdmin ||
    !aclLoaded ||
    hasPermission(loggedInUserACL, MODULE_ACL.AI_AGENT, 'read') ||
    hasPermission(loggedInUserACL, MODULE_ACL.AI_AGENT, 'view_permission');
  const canAdd = !isSuperAdmin && (isCallCentreAdmin || aclAllows('add'));
  const canEdit = !isSuperAdmin && (isCallCentreAdmin || aclAllows('edit'));
  const canDelete = !isSuperAdmin && (isCallCentreAdmin || aclAllows('delete'));

  const openDetails = (row: any) => {
    setDetailsModal({ id: kbId(row), name: kbName(row) });
  };

  const applyLocalEdit = (updatedRow: any) => {
    const id = kbId(updatedRow);
    setRows((prev) => prev.map((r) => (kbId(r) === id ? { ...r, ...updatedRow } : r)));
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const id = kbId(pendingDelete);
    setDeleting(true);
    deleteKnowledgeBase(id)
      .then(() => {
        toast.success('Knowledge base deleted');
        setPendingDelete(null);
        fetchList();
      })
      .catch((err: any) => toast.error(typeof err === 'string' ? err : 'Failed to delete knowledge base'))
      .finally(() => setDeleting(false));
  };

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        id: 'kb_name',
        accessorFn: (row: any) => kbName(row) || '',
        header: 'Name',
        cell: ({ row }) => (
          <span className="font-medium text-foreground">{kbName(row.original)}</span>
        ),
      },
      {
        id: 'kb_type',
        accessorFn: (row: any) => kbType(row) || '',
        header: 'Type',
        cell: ({ row }) => {
          const type = kbType(row.original);
          return type ? (
            <Badge variant="outline">{type}</Badge>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
      {
        id: 'status',
        accessorFn: (row: any) => kbStatus(row) || '',
        header: 'Status',
        cell: ({ row }) => {
          const status = kbStatus(row.original);
          return status ? (
            <Badge variant={statusBadgeVariant(status)}>{status}</Badge>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
     
      ...(isSuperAdmin
        ? [
            {
              id: 'created_by',
              accessorFn: (row: any) => row.created_by_name || row.created_by_username || '',
              header: 'Created By',
              cell: ({ row }) => {
                const name = row.original.created_by_name;
                const username = row.original.created_by_username;
                if (!name && !username) return <span className="text-xs text-muted-foreground">—</span>;
                return (
                  <div className="leading-tight">
                    <div className="text-sm text-foreground">{name || username}</div>
                    {name && username && name !== username && (
                      <div className="text-[11px] text-muted-foreground">@{username}</div>
                    )}
                  </div>
                );
              },
            },
          ]
        : []),
      {
        id: 'created_at',
        accessorFn: (row: any) => {
          const created = kbCreatedAt(row);
          if (!created) return '';
          return Number.isNaN(Date.parse(created)) ? created : new Date(created).toLocaleString();
        },
        header: 'Created',
        cell: ({ row }) => {
          const created = kbCreatedAt(row.original);
          return created ? (
            <span className="text-xs text-muted-foreground">
              {Number.isNaN(Date.parse(created)) ? created : new Date(created).toLocaleString()}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
      {
        id: 'updated_at',
        accessorFn: (row: any) => {
          const updated = kbUpdatedAt(row);
          if (!updated) return '';
          return Number.isNaN(Date.parse(updated)) ? updated : new Date(updated).toLocaleString();
        },
        header: 'Updated',
        cell: ({ row }) => {
          const updated = kbUpdatedAt(row.original);
          return updated ? (
            <span className="text-xs text-muted-foreground">
              {Number.isNaN(Date.parse(updated)) ? updated : new Date(updated).toLocaleString()}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      },
      {
        id: 'actions',
        header: 'actions',
        size: 60,
        enableHiding: false,
        enableSorting: false,
        enableGlobalFilter: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>{kbName(row.original)}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => openDetails(row.original)}>
                <Eye className="h-4 w-4" />
                View Details
              </DropdownMenuItem>
              {canAdd && (
                <DropdownMenuItem onClick={() => setPendingAddDoc(row.original)}>
                  <FilePlus2 className="h-4 w-4" />
                  Add Document
                </DropdownMenuItem>
              )}
              {canEdit && (
                <DropdownMenuItem onClick={() => setPendingEdit(row.original)}>
                  <Pencil className="h-4 w-4" />
                  Edit
                </DropdownMenuItem>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => setPendingDelete(row.original)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ];
    return cols;
  }, [canAdd, canEdit, canDelete, isSuperAdmin]);

  if (aclLoaded && !canRead) {
    return (
      <FillPage>
        <PageHeader title="Knowledge Base" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  if (loading) return <Loader />;

  return (
    <FillPage>
      <PageHeader
        title="Knowledge Base"
        description="Documents and sources the AI Agent can answer from."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={fetchList} disabled={loading} title="Refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            {canAdd && (
              <Button asChild>
                <Link to={`/${ROUTES.AI_AGENT.KNOWLEDGE_BASE_CREATE}`}>
                  <Plus className="h-4 w-4" />
                  New
                </Link>
              </Button>
            )}
          </div>
        }
      />

      <DataTable
        tableId="ai-agent:knowledge-base:list:v1"
        columns={columns}
        data={rows}
        isLoading={loading}
        searchPlaceholder="Search knowledge bases…"
        emptyTitle="No knowledge bases yet"
        emptyDescription="Create one to start feeding documents to the AI Agent."
        getRowId={(row: any) => String(kbId(row) || Math.random())}
        initialColumnPinning={{ right: ['actions'] }}
        pageSize={10}
        exportFilename="knowledge-bases.csv"
      />

      <AddDocumentDialog
        open={pendingAddDoc !== null}
        onOpenChange={(o: boolean) => !o && setPendingAddDoc(null)}
        kbId={pendingAddDoc ? kbId(pendingAddDoc) : ''}
        kbName={pendingAddDoc ? kbName(pendingAddDoc) : ''}
        onAdded={fetchList}
      />

      <EditKnowledgeBaseDialog
        open={pendingEdit !== null}
        onOpenChange={(o: boolean) => !o && setPendingEdit(null)}
        row={pendingEdit}
        onSave={applyLocalEdit}
      />

      <Dialog open={pendingDelete !== null} onOpenChange={(o) => !o && !deleting && setPendingDelete(null)}>
        <KbDialogContent className="sm:max-w-md">
          <KbDialogHead icon={Trash2} danger title="Delete this knowledge base?" />
          <p className="ags-dlgbody">
            Knowledge base <b>{kbName(pendingDelete || {})}</b> and its ingested documents will be permanently deleted.
          </p>
          <div className="ags-dlgfoot">
            <button type="button" className="ags-btn" onClick={() => setPendingDelete(null)} disabled={deleting}>
              Cancel
            </button>
            <button type="button" className="ags-btn ags-red" onClick={confirmDelete} disabled={deleting}>
              {deleting && <Loader2 className="ags-i animate-spin" />}
              Delete
            </button>
          </div>
        </KbDialogContent>
      </Dialog>

      <Dialog open={detailsModal !== null} onOpenChange={(o) => !o && setDetailsModal(null)}>
        <KbDialogContent className="sm:max-w-md">
          <KbDialogHead icon={Eye} title="Knowledge base details" sub="Name and ID of this knowledge base." />
          <div className="ags-kv">
            <small>Name</small>
            <div>{detailsModal?.name || '—'}</div>
          </div>
          <div className="ags-kv">
            <small>ID</small>
            <div className="ags-mono">{detailsModal?.id || '—'}</div>
          </div>
          <div className="ags-dlgfoot">
            <button type="button" className="ags-btn" onClick={() => setDetailsModal(null)}>
              Close
            </button>
          </div>
        </KbDialogContent>
      </Dialog>
    </FillPage>
  );
};

export default KnowledgeBasePage;
